import { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, message, Button } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  list_ai_providers,
  create_ai_provider,
  update_ai_provider,
  delete_ai_provider,
  toggle_ai_provider,
  update_ai_provider_model,
  delete_ai_provider_model,
} from "../api/admin.apis";
import {
  AiProvidersTable,
  CreateProviderModal,
  EditProviderModal,
  ProviderModelsDrawer,
  ModelFormModal,
} from "../components/ai-providers";
import { getUserRole } from "../utils/token";

const SUPER_ADMIN_ROLES = ["super_admin", "Super Admin", "super-admin"];

const extractApiError = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (data.errors && typeof data.errors === "object") {
    const firstKey = Object.keys(data.errors)[0];
    if (firstKey) return data.errors[firstKey];
  }
  return data.message || fallback;
};

function AiProviders() {
  const userRole = getUserRole();
  const isSuperAdmin = SUPER_ADMIN_ROLES.includes(userRole);

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  // create
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // edit
  const [editingProvider, setEditingProvider] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // toggle
  const [togglingKey, setTogglingKey] = useState(null);

  // models drawer
  const [drawerProvider, setDrawerProvider] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [togglingModelId, setTogglingModelId] = useState(null);
  const [deletingModelId, setDeletingModelId] = useState(null);

  // model form (add/edit) — the modal owns its own save flow
  const [modelFormVisible, setModelFormVisible] = useState(false);
  const [modelFormMode, setModelFormMode] = useState("create");
  const [editingModel, setEditingModel] = useState(null);

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await list_ai_providers();
      if (response.data.status === "success") {
        const list = response.data.data?.providers || [];
        setProviders(list);
        // Keep the drawer in sync if it's open
        setDrawerProvider((prev) =>
          prev ? list.find((p) => p.provider_key === prev.provider_key) || null : prev
        );
      } else {
        message.error(response.data.message || "Failed to load providers");
      }
    } catch (error) {
      console.error(error);
      message.error(extractApiError(error, "Error loading AI providers"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // ── Provider CRUD ─────────────────────────────────────────────────────────

  const handleCreateProvider = async (payload) => {
    try {
      setCreateLoading(true);
      const response = await create_ai_provider(payload);
      if (response.data.status === "success") {
        message.success(response.data.message || "Provider created");
        setCreateModalVisible(false);
        fetchProviders();
      } else {
        message.error(response.data.message || "Failed to create provider");
      }
    } catch (error) {
      console.error(error);
      message.error(extractApiError(error, "Failed to create AI provider"));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditOpen = (provider) => {
    setEditingProvider(provider);
    setEditModalVisible(true);
  };

  const handleEditProvider = async (payload) => {
    try {
      setEditLoading(true);
      const response = await update_ai_provider(editingProvider.provider_key, payload);
      if (response.data.status === "success") {
        message.success(response.data.message || "Provider updated");
        setEditModalVisible(false);
        setEditingProvider(null);
        fetchProviders();
      } else {
        message.error(response.data.message || "Failed to update provider");
      }
    } catch (error) {
      console.error(error);
      message.error(extractApiError(error, "Failed to update AI provider"));
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteProvider = async (provider) => {
    try {
      const response = await delete_ai_provider(provider.provider_key);
      if (response.data.status === "success") {
        message.success(response.data.message || "Provider deleted");
        fetchProviders();
      } else {
        message.error(response.data.message || "Failed to delete provider");
      }
    } catch (error) {
      console.error(error);
      message.error(extractApiError(error, "Failed to delete AI provider"));
    }
  };

  const handleToggleProvider = async (provider) => {
    try {
      setTogglingKey(provider.provider_key);
      const response = await toggle_ai_provider(provider.provider_key, !provider.enabled);
      if (response.data.status === "success") {
        message.success(
          response.data.message ||
            `Provider ${provider.enabled ? "disabled" : "enabled"}`
        );
        fetchProviders();
      } else {
        message.error(response.data.message || "Failed to toggle provider");
      }
    } catch (error) {
      console.error(error);
      message.error(extractApiError(error, "Failed to toggle AI provider"));
    } finally {
      setTogglingKey(null);
    }
  };

  // ── Models CRUD ───────────────────────────────────────────────────────────

  const handleOpenDrawer = (provider) => {
    setDrawerProvider(provider);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setDrawerProvider(null);
  };

  const openAddModel = () => {
    setModelFormMode("create");
    setEditingModel(null);
    setModelFormVisible(true);
  };

  const openEditModel = (model) => {
    setModelFormMode("edit");
    setEditingModel(model);
    setModelFormVisible(true);
  };

  const closeModelForm = () => {
    setModelFormVisible(false);
    setEditingModel(null);
  };

  const handleToggleModel = async (model, checked) => {
    if (!drawerProvider) return;
    try {
      setTogglingModelId(model.model_id);
      const response = await update_ai_provider_model(
        drawerProvider.provider_key,
        model.model_id,
        { enabled: checked }
      );
      if (response.data.status === "success") {
        message.success(`Model ${checked ? "enabled" : "disabled"}`);
        fetchProviders();
      } else {
        message.error(response.data.message || "Failed to toggle model");
      }
    } catch (error) {
      console.error(error);
      message.error(extractApiError(error, "Failed to toggle model"));
    } finally {
      setTogglingModelId(null);
    }
  };

  const handleDeleteModel = async (model) => {
    if (!drawerProvider) return;
    try {
      setDeletingModelId(model.model_id);
      const response = await delete_ai_provider_model(
        drawerProvider.provider_key,
        model.model_id
      );
      if (response.data.status === "success") {
        message.success(response.data.message || "Model removed");
        fetchProviders();
      } else {
        message.error(response.data.message || "Failed to remove model");
      }
    } catch (error) {
      console.error(error);
      message.error(extractApiError(error, "Failed to remove model"));
    } finally {
      setDeletingModelId(null);
    }
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>AI Providers ({providers.length})</span>
                <div>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchProviders}
                    style={{ marginRight: 8 }}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                  >
                    Add Provider
                  </Button>
                </div>
              </div>
            }
          >
            <AiProvidersTable
              providers={providers}
              loading={loading}
              onEdit={handleEditOpen}
              onDelete={handleDeleteProvider}
              onToggle={handleToggleProvider}
              togglingKey={togglingKey}
              onManageModels={handleOpenDrawer}
              canDelete={isSuperAdmin}
            />
          </Card>
        </Col>
      </Row>

      <CreateProviderModal
        visible={createModalVisible}
        loading={createLoading}
        onSubmit={handleCreateProvider}
        onClose={() => setCreateModalVisible(false)}
      />

      <EditProviderModal
        visible={editModalVisible}
        loading={editLoading}
        provider={editingProvider}
        onSubmit={handleEditProvider}
        onClose={() => {
          setEditModalVisible(false);
          setEditingProvider(null);
        }}
      />

      <ProviderModelsDrawer
        visible={drawerVisible}
        provider={drawerProvider}
        onClose={handleCloseDrawer}
        onAddModel={openAddModel}
        onEditModel={openEditModel}
        onDeleteModel={handleDeleteModel}
        onToggleModel={handleToggleModel}
        togglingModelId={togglingModelId}
        deletingModelId={deletingModelId}
      />

      <ModelFormModal
        visible={modelFormVisible}
        mode={modelFormMode}
        providerKey={drawerProvider?.provider_key}
        model={editingModel}
        onSaved={fetchProviders}
        onClose={closeModelForm}
      />
    </div>
  );
}

export default AiProviders;
